import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle } from "../components/ui/card";
import { Loader2, ArrowLeft, Play, Download, Calendar, HardDrive } from "lucide-react";
import AuthGuard from "../components/auth-guard";

interface AudioFile {
  fileName: string;
  filePath: string;
  size: number;
  createdAt: number;
  modifiedAt: number;
  sessionId: string;
  timestamp: number;
}

interface AudioFilesResponse {
  files: AudioFile[];
}

export default function AudioFiles() {
  return (
    <AuthGuard>
      <AudioFilesContent />
    </AuthGuard>
  );
}

function AudioFilesContent() {
  const [, setLocation] = useLocation();

  const { data, isLoading, error, refetch } = useQuery<AudioFilesResponse>({
    queryKey: ["/api/audio-files"],
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const handlePlay = (filePath: string) => {
    const audio = new Audio(filePath);
    audio.play().catch((err) => {
      console.error("音声再生エラー:", err);
    });
  };

  const handleDownload = (filePath: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = filePath;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => setLocation("/select-filter")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          条件選択に戻る
        </Button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          ユーザー音声ファイル一覧
        </h1>
        <p className="text-medical-gray">
          蓄積されたユーザーの音声ファイルを確認できます
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-medical-blue" />
          <span className="ml-3 text-medical-gray">音声ファイルを読み込み中...</span>
        </div>
      ) : error ? (
        <Card className="p-6">
          <div className="text-center text-red-600">
            <p>音声ファイルの取得に失敗しました</p>
            <Button onClick={() => refetch()} className="mt-4">
              再試行
            </Button>
          </div>
        </Card>
      ) : data?.files.length === 0 ? (
        <Card className="p-6">
          <div className="text-center text-medical-gray">
            <p>音声ファイルがありません</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <HardDrive className="h-5 w-5 text-medical-blue" />
                <span className="font-semibold">
                  合計 {data?.files.length || 0} ファイル
                </span>
              </div>
              <Button onClick={() => refetch()} variant="outline" size="sm">
                更新
              </Button>
            </div>
          </div>

          {data?.files.map((file) => (
            <Card key={file.fileName} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{file.fileName}</CardTitle>
                    <div className="flex flex-wrap gap-4 text-sm text-medical-gray">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(file.timestamp)}
                      </div>
                      <div className="flex items-center">
                        <HardDrive className="h-4 w-4 mr-1" />
                        {formatFileSize(file.size)}
                      </div>
                      {file.sessionId && (
                        <div className="text-xs bg-gray-100 px-2 py-1 rounded">
                          セッション: {file.sessionId.substring(0, 20)}...
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePlay(file.filePath)}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      再生
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(file.filePath, file.fileName)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      ダウンロード
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
